import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface WebsiteHeroImage {
  id: string;
  sort_order: number;
  url: string;
  created_at: string;
}

export function useWebsiteHeroImages() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['websiteHeroImages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_hero_images')
        .select('id, sort_order, url, created_at')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as WebsiteHeroImage[];
    },
  });

  const addMutation = useMutation({
    mutationFn: async (url: string) => {
      const { data: list } = await supabase
        .from('website_hero_images')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);
      const nextOrder = (list?.[0]?.sort_order ?? -1) + 1;
      const { data, error } = await supabase
        .from('website_hero_images')
        .insert({ url: url.trim(), sort_order: nextOrder })
        .select()
        .single();
      if (error) throw error;
      return data as WebsiteHeroImage;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteHeroImages'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('website_hero_images').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteHeroImages'] }),
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ id, sort_order }: { id: string; sort_order: number }) => {
      const { error } = await supabase
        .from('website_hero_images')
        .update({ sort_order })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteHeroImages'] }),
  });

  return {
    images: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    addImage: addMutation.mutateAsync,
    addLoading: addMutation.isPending,
    deleteImage: deleteMutation.mutateAsync,
    deleteLoading: deleteMutation.isPending,
    reorder: reorderMutation.mutateAsync,
  };
}
